import { useState, useEffect, useRef } from 'preact/hooks'
import { globalEmitter, emitterProps } from 'helpers/emitter-event'
import { addMultipleCartItems, updateMultipleCartItems, updateCartItem, updateCart } from 'libs/cart'
import { hideModal } from 'helpers/dom'
import Icon from 'snippets/icon/icon'

const PersonaliseContentModal = ({ modalData, product, status, existingCharms, editingCharmPosition, cartItem, showOnboarding, onDismissOnboarding }) => {
  const [activeStep, setActiveStep] = useState(1)
  const [selectedCategory, setSelectedCategory] = useState(0)
  const [charm1, setCharm1] = useState(null)
  const [charm2, setCharm2] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const videoRef = useRef(null)

  const styles = modalData?.styles?.filter(s => s.styleName) || []
  const tabHeader1 = modalData?.tabHeader1 || translate?.general?.personalise?.tab_header_1 || 'CHARM 1'
  const tabHeader2 = modalData?.tabHeader2 || translate?.general?.personalise?.tab_header_2 || 'CHARM 2'
  const styleLabel = modalData?.styleLabel || translate?.general?.personalise?.style_label || 'STYLE'
  const selectionLabel = modalData?.selectionLabel || translate?.general?.personalise?.selection_label || 'SELECTION'
  const selectedLabel = modalData?.selectedLabel || translate?.general?.personalise?.selected_label || 'SELECTED'
  const addToCartCta1 = modalData?.addToCartCta1 || translate?.general?.personalise?.add_to_cart_cta1 || 'ADD TO CART'
  const addToCartCta2 = modalData?.addToCartCta2 || translate?.general?.personalise?.add_to_cart_cta2 || 'CONFIRM SELECTION'
  const addNextCta = modalData?.addNextCta || translate?.general?.personalise?.add_next_cta || 'ADD NEXT CHARM'
  const editCta = modalData?.editCta || translate?.general?.personalise?.edit_cta || 'EDIT CHARM 1'
  const instructionsBanner = modalData?.instructionsBanner || translate?.general?.personalise?.instructions_banner || 'Your chosen charms will be securely welded to the clasp of your bracelet, adding a sentimental detail to treasure.'
  const instructionsVideo = modalData?.instructionsVideo
  const placeholderImage = modalData?.placeholderImage || 'https://cdn.shopify.com/s/files/1/1025/4627/files/charm-placeholder.png?v=1770004056'

  // Initialize with existing charms when editing
  useEffect(() => {
    if (status === 'Edit' && existingCharms?.length > 0) {
      const c1 = existingCharms.find(c => c.charmPosition === '1')
      const c2 = existingCharms.find(c => c.charmPosition === '2')

      // If only one charm exists, always treat it as charm1 regardless of original position
      if (existingCharms.length === 1) {
        const singleCharm = existingCharms[0]

        setCharm1(singleCharm)
        setCharm2(null)
        setActiveStep(1)
        setIsEditMode(false)
      } else {
        // Two charms exist
        if (c1) {
          setCharm1(c1)
        }

        if (c2) {
          setCharm2(c2)
        }

        // Set active step based on which charm is being edited
        if (editingCharmPosition === '2') {
          setActiveStep(2)
        } else {
          setActiveStep(1)
        }

        // Only enable full edit mode if there are 2 charms
        setIsEditMode(c1 && c2)
      }

      setSelectedCategory(0)
    } else {
      // Reset state for new personalisation
      setCharm1(null)
      setCharm2(null)
      setActiveStep(1)
      setSelectedCategory(0)
      setIsEditMode(false)
    }

    return () => null
  }, [status, existingCharms, editingCharmPosition, modalData])

  // Handle video playback
  useEffect(() => {
    const video = videoRef.current

    if (video && showOnboarding) {
      video.play().catch(() => {})
    }

    return () => {
      if (video) {
        video.pause()
      }
    }
  }, [showOnboarding, instructionsVideo])

  // Dismiss onboarding when clicking category
  const handleCategoryClick = (index) => {
    setSelectedCategory(index)
    onDismissOnboarding?.()
  }

  const handleSelectCharm = (variant, styleName) => {
    // Dismiss onboarding when selecting a variant
    onDismissOnboarding?.()

    const charmData = {
      variantId: variant.id,
      title: variant.title,
      price: variant.price,
      priceFormatted: variant.priceFormatted,
      image: variant.image?.src,
      sku: variant.sku,
      styleName
    }

    if (activeStep === 1) {
      setCharm1(charmData)

      // Clear charm2 when re-selecting charm1, but only if NOT in Edit mode
      if (!isEditMode) {
        setCharm2(null)
      }
    } else {
      setCharm2(charmData)
    }
  }

  const handleAddNext = () => {
    if (charm1) {
      setActiveStep(2)
    }
  }

  const handleEditCharm1 = () => {
    setActiveStep(1)
  }

  const handleAddToCart = async (withSecondCharm = false) => {
    setIsLoading(true)

    try {
      const parentSku = product?.variants?.[0]?.sku || product?.sku
      const timestamp = Date.now().toString()

      if (status === 'Edit' && cartItem) {
        // Edit mode - remove old charms first, then add new ones
        const updates = {}

        // Remove existing charms (handle consolidated items with _allKeys from discount-split duplicates)
        if (existingCharms?.length > 0) {
          existingCharms.forEach(charm => {
            const charmKeys = charm._allKeys || [charm.key]

            charmKeys.forEach(key => {
              if (key) {
                updates[key] = 0
              }
            })
          })
        }

        await updateMultipleCartItems({ updates })

        // Get the timestamp from the parent cart item to maintain grouping
        const parentTimestamp = cartItem?.properties?._timestamp || timestamp

        // Add new charms linked to parent by SKU and timestamp
        const items = []

        if (charm1) {
          items.push({
            id: charm1.variantId,
            quantity: cartItem?.quantity || 1,
            properties: {
              _isPersonaliseCharm: 'true',
              _parentProductSku: parentSku,
              _charmPosition: '1',
              _timestamp: parentTimestamp,
              For: product?.title
            }
          })
        }

        if (withSecondCharm && charm2) {
          items.push({
            id: charm2.variantId,
            quantity: cartItem?.quantity || 1,
            properties: {
              _isPersonaliseCharm: 'true',
              _parentProductSku: parentSku,
              _charmPosition: '2',
              _timestamp: parentTimestamp,
              For: product?.title
            }
          })
        }

        if (items.length > 0) {
          await addMultipleCartItems({ items })
        }
      } else if (status === 'AddFromCart' && cartItem) {
        // Adding charms to existing cart item - use parent's timestamp for grouping
        let parentTimestamp = cartItem?.properties?._timestamp

        // If parent has no timestamp, add one so charms can be grouped with it
        // Without this, removeOrphanedCharms will delete the charms immediately
        if (!parentTimestamp) {
          parentTimestamp = timestamp

          const updatedProperties = { ...(cartItem?.properties || {}), _timestamp: parentTimestamp }

          await updateCartItem(cartItem.key, cartItem.quantity, updatedProperties)
        }

        const items = []

        if (charm1) {
          items.push({
            id: charm1.variantId,
            quantity: cartItem?.quantity || 1,
            properties: {
              _isPersonaliseCharm: 'true',
              _parentProductSku: parentSku,
              _charmPosition: '1',
              _timestamp: parentTimestamp,
              For: product?.title
            }
          })
        }

        if (withSecondCharm && charm2) {
          items.push({
            id: charm2.variantId,
            quantity: cartItem?.quantity || 1,
            properties: {
              _isPersonaliseCharm: 'true',
              _parentProductSku: parentSku,
              _charmPosition: '2',
              _timestamp: parentTimestamp,
              For: product?.title
            }
          })
        }

        if (items.length > 0) {
          await addMultipleCartItems({ items })
        }
      } else {
        // New personalisation from PDP - add product and charms with shared timestamp
        const items = [
          {
            id: product?.variants?.[0]?.id || product?.id,
            quantity: 1,
            properties: {
              _timestamp: timestamp
            }
          }
        ]

        if (charm1) {
          items.push({
            id: charm1.variantId,
            quantity: 1,
            properties: {
              _isPersonaliseCharm: 'true',
              _parentProductSku: parentSku,
              _charmPosition: '1',
              _timestamp: timestamp,
              For: product?.title
            }
          })
        }

        if (withSecondCharm && charm2) {
          items.push({
            id: charm2.variantId,
            quantity: 1,
            properties: {
              _isPersonaliseCharm: 'true',
              _parentProductSku: parentSku,
              _charmPosition: '2',
              _timestamp: timestamp,
              For: product?.title
            }
          })
        }

        await addMultipleCartItems({ items })
      }

      // Fetch updated cart and open mini cart
      const newCart = await updateCart()

      // Update global cart state
      if (typeof GM_STATE !== 'undefined') {
        GM_STATE.cart.initCart = newCart
      }

      hideModal('personalise-product-modal')
      globalEmitter.emit(emitterProps.cart.add, newCart)
    } catch (error) {
      console.error('Error adding personalised items to cart:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getCurrentVariants = () => {
    if (styles.length === 0) return []

    const currentStyle = styles[selectedCategory]

    return currentStyle?.selectionProduct?.variants || []
  }

  const isVariantSelected = (variantId) => {
    if (activeStep === 1) {
      return charm1?.variantId === variantId
    }

    return charm2?.variantId === variantId
  }

  const getSelectedCharmForStep = () => {
    return activeStep === 1 ? charm1 : charm2
  }

  const currentCharm = getSelectedCharmForStep()

  // Get the preview image for charm1 (selected or placeholder)
  const charm1PreviewImage = charm1?.image || placeholderImage
  // Get the preview image for charm2 (selected or placeholder)
  const charm2PreviewImage = charm2?.image || placeholderImage

  return (
    <div className="c-personalise-modal flex flex-col h-full bg-white">
      {/* Charm Tabs - sticky at top */}
      <div className="c-personalise-modal__tabs flex border-b border-[#e7e7e7] h-[44px] flex-shrink-0 bg-white">
        <button
          type="button"
          className={`flex-1 h-full px-4 text-xs uppercase tracking-[1px] flex items-center justify-center gap-1.5 transition-colors ${
            activeStep === 1 ? 'border-b-2 border-black' : 'hover:border-b-[1px] hover:border-black'
          }`}
          onClick={() => setActiveStep(1)}
        >
          {/* Checkmark shows when charm1 is selected (in Edit mode: always when selected, normal mode: only on step 2) */}
          {(isEditMode ? charm1 : (activeStep === 2 && charm1)) && <Icon name="icon-checkmark" className="w-4 h-4" viewBox="0 0 16 16" />}
          {tabHeader1}
        </button>
        <button
          type="button"
          className={`flex-1 h-full px-4 text-xs uppercase tracking-[1px] flex items-center justify-center gap-1.5 transition-colors ${
            activeStep === 2 ? 'border-b-2 border-black' : isEditMode ? 'hover:border-b-[1px] hover:border-black' : 'text-grey-400 hover:border-b-[1px] hover:border-black'
          }`}
          onClick={() => (isEditMode || activeStep === 2) && setActiveStep(2)}
          disabled={!isEditMode && activeStep !== 2}
        >
          {/* Checkmark shows when charm2 is selected (in Edit mode: always when selected, normal mode: only on step 2) */}
          {(isEditMode ? charm2 : (activeStep === 2 && charm2)) && <Icon name="icon-checkmark" className="w-4 h-4" viewBox="0 0 16 16" />}
          {tabHeader2}
        </button>
      </div>

      {/* Scrollable content wrapper */}
      <div className="c-personalise-modal__content flex-1 overflow-y-auto min-h-0" body-scroll-lock-ignore>
        {/* Preview wrapper with Video overlay */}
        <div className="c-personalise-modal__preview-wrapper relative overflow-hidden">

        {/* Image Preview Panel */}
        <div className="c-personalise-modal__preview">
          {/* Charms Preview */}
          <div className="c-personalise-modal__selected-preview flex h-[266px]">
            {/* Charm 1 Image - shrinks from right to left when going to step 2 (or in Edit mode, always 50%) */}
            <div
              className={`relative overflow-hidden border-r border-white transition-all duration-500 ease-out bg-[var(--alphas-black-5,_rgba(0,0,0,0.05))] ${
                (isEditMode || activeStep === 2) ? 'w-1/2' : 'w-full'
              }`}
            >
              {charm1PreviewImage ? (
                <img
                  src={charm1PreviewImage}
                  alt={charm1?.title || 'Charm preview'}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-[#f0f0f0]" />
              )}
              {/* SELECTED label shows when editing (previously selected) or when on step 2 in normal mode */}
              {(status === 'Edit' ? charm1 : (activeStep === 2 && charm1)) && (
                <div className="absolute bottom-2 left-2 right-2 bg-white/50 text-center">
                  <span className="text-xs text-[#494949] leading-[1.8] uppercase">{selectedLabel}</span>
                </div>
              )}
            </div>

            {/* Charm 2 Image - slides in from right when going to step 2 (or in Edit mode, always visible) */}
            <div
              className={`relative overflow-hidden border-l border-white transition-all duration-500 ease-out bg-[var(--alphas-black-5,_rgba(0,0,0,0.05))] ${
                (isEditMode || activeStep === 2) ? 'w-1/2 opacity-100' : 'w-0 opacity-0'
              }`}
            >
              {charm2PreviewImage ? (
                <img
                  src={charm2PreviewImage}
                  alt={charm2?.title || 'Select charm 2'}
                  className="w-full h-full object-contain"
                />
              ) : placeholderImage ? (
                <img
                  src={placeholderImage}
                  alt="Select your second charm"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-[#f0f0f0]" />
              )}
              {/* SELECTED label shows when editing (previously selected) */}
              {status === 'Edit' && charm2 && (
                <div className="absolute bottom-2 left-2 right-2 bg-white/50 text-center">
                  <span className="text-xs text-[#494949] leading-[1.8] uppercase">{selectedLabel}</span>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Video + Banner overlay - absolutely positioned to cover tabs + preview, slides up when dismissed */}
        <div
          className={`c-personalise-modal__video-overlay absolute inset-0 bg-white transition-transform duration-500 ease-out ${
            !showOnboarding ? '-translate-y-full' : 'translate-y-0'
          }`}
          style={{ zIndex: 10 }}
        >
          {instructionsVideo && (
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              src={instructionsVideo}
              muted
              loop
              playsInline
              autoPlay
            />
          )}
          {/* Banner - absolute bottom of video overlay */}
          <div className="c-personalise-modal__banner absolute bottom-0 left-0 right-0 w-full bg-[#f0f0f0] border-b border-[#e4e4e4] px-4 py-2 text-xs text-center leading-[1.8]">
            {instructionsBanner}
          </div>
        </div>
        </div>

        {/* Style Categories */}
        {styles.length > 0 && (
          <div className="c-personalise-modal__categories p-4">
            <div className="text-xs uppercase tracking-[0.96px] leading-[20px] mb-2">{styleLabel}:</div>
            <div className="flex gap-3">
              {styles.map((style, index) => (
                <button
                  key={index}
                  type="button"
                  className={`flex-1 py-[18px] px-2 h-[48px] text-xs font-medium uppercase tracking-[0.96px] leading-3 border transition-colors ${
                    selectedCategory === index
                      ? 'border-black bg-black/5'
                      : 'border-[#e4e4e4] bg-white hover:border-grey-500'
                  }`}
                  onClick={() => handleCategoryClick(index)}
                >
                  {style.styleName}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Product Grid */}
        <div className="c-personalise-modal__grid px-4 pb-6">
          <div className="flex items-start justify-between text-xs uppercase tracking-[0.96px] leading-[20px] mb-2">
            <span>{selectionLabel}:</span>
            {currentCharm && (
              <div className="flex gap-3 font-semibold">
                <span>{currentCharm.title}</span>
                <span>{currentCharm.priceFormatted}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-6 gap-0.5">
            {getCurrentVariants().filter(v => v.available).length === 0 ? (
              <div className="col-span-6 py-8 text-center text-grey-500 text-sm">
                No charms available for this style
              </div>
            ) : (
              getCurrentVariants().filter(v => v.available).map((variant) => (
                <button
                  key={variant.id}
                  type="button"
                  className={`c-personalise-modal__variant relative overflow-hidden border transition-colors ${
                    isVariantSelected(variant.id)
                      ? 'border-black/90'
                      : 'border-black/10 hover:border-black/30'
                  }`}
                  onClick={() => handleSelectCharm(variant, styles[selectedCategory]?.styleName)}
                >
                  <div className={`aspect-[63/79] relative overflow-hidden ${isVariantSelected(variant.id) ? 'bg-black/5' : 'bg-white'}`}>
                    {variant.image?.src && (
                      <img
                        src={variant.image.src}
                        alt={variant.image.alt || variant.title}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons - outside scrollable area */}
      <div className="c-personalise-modal__actions p-4 border-t border-[#e4e4e4] flex-shrink-0">
          <div className="flex gap-3">
            {isEditMode ? (
              // Edit mode from cart with 2 charms - show switch charm button
              <>
                <button
                  type="button"
                  className="flex-1 basis-0 h-12 border border-[#E4E4E4] text-xs font-medium uppercase tracking-[0.96px] leading-[1.1] flex items-center justify-center gap-2 hover:bg-black/5 transition-colors"
                  onClick={() => setActiveStep(activeStep === 1 ? 2 : 1)}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <Icon name="icon-switch" className="w-[11.667px] h-[11.667px]" viewBox="0 0 20 20" />
                  </span>
                  {activeStep === 1 ? 'EDIT CHARM 2' : 'EDIT CHARM 1'}
                </button>
                <button
                  type="button"
                  className="flex-1 basis-0 h-12 bg-black text-white text-xs font-medium uppercase tracking-[0.96px] leading-[1.1] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/90 transition-colors"
                  onClick={() => handleAddToCart(true)}
                  disabled={(!charm1 && !charm2) || isLoading}
                >
                  {isLoading ? 'Saving...' : 'CONFIRM SELECTION'}
                </button>
              </>
            ) : status === 'Edit' && activeStep === 1 ? (
              // Edit mode from cart with 1 charm - show add next or confirm
              <>
                <button
                  type="button"
                  className="flex-1 basis-0 h-12 border border-[#E4E4E4] text-xs font-medium uppercase tracking-[0.96px] leading-[1.1] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 transition-colors"
                  onClick={handleAddNext}
                  disabled={!charm1 || isLoading}
                >
                  {addNextCta}
                  <Icon name="icon-arrow-right" className="w-[11.6px] h-[11.6px]" viewBox="0 0 16 16" />
                </button>
                <button
                  type="button"
                  className="flex-1 basis-0 h-12 bg-black text-white text-xs font-medium uppercase tracking-[0.96px] leading-[1.1] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/90 transition-colors"
                  onClick={() => handleAddToCart(false)}
                  disabled={!charm1 || isLoading}
                >
                  {isLoading ? 'Saving...' : 'CONFIRM SELECTION'}
                </button>
              </>
            ) : activeStep === 1 ? (
              // Step 1 from PDP - Add to cart or add next charm
              <>
                <button
                  type="button"
                  className="flex-1 basis-0 h-12 border border-[#E4E4E4] text-xs font-medium uppercase tracking-[0.96px] leading-[1.1] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 transition-colors"
                  onClick={() => handleAddToCart(false)}
                  disabled={!charm1 || isLoading}
                >
                  {isLoading ? 'Adding...' : addToCartCta1}
                </button>
                <button
                  type="button"
                  className="flex-1 basis-0 h-12 bg-black text-white text-xs font-medium uppercase tracking-[0.96px] leading-[1.1] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/90 transition-colors"
                  onClick={handleAddNext}
                  disabled={!charm1 || isLoading}
                >
                  {addNextCta}
                  <Icon name="icon-arrow-right" className="w-[11.6px] h-[11.6px]" viewBox="0 0 16 16" />
                </button>
              </>
            ) : (
              // Step 2 - Edit charm 1 or confirm
              <>
                <button
                  type="button"
                  className="flex-1 basis-0 h-12 border border-[#E4E4E4] text-xs font-medium uppercase tracking-[0.96px] leading-[1.1] flex items-center justify-center gap-2 hover:bg-black/5 transition-colors"
                  onClick={handleEditCharm1}
                >
                  <span className="w-5 h-5 flex items-center justify-center"><Icon name="icon-arrow-left" className="w-[11.667px] h-[11.667px]" viewBox="0 0 20 20" /></span>
                  {editCta}
                </button>
                <button
                  type="button"
                  className="flex-1 basis-0 h-12 bg-black text-white text-xs font-medium uppercase tracking-[0.96px] leading-[1.1] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/90 transition-colors"
                  onClick={() => handleAddToCart(true)}
                  disabled={!charm2 || isLoading}
                >
                  {isLoading ? 'Adding...' : addToCartCta2}
                </button>
              </>
            )}
          </div>
      </div>
    </div>
  )
}

export default PersonaliseContentModal
